import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import {
  AlertTriangle,
  ClipboardList,
  HeartPulse,
  Mic,
  Sparkles
} from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'

import { useAuth } from '@/context/AuthContext'
import { buildClinicalDashboardSummary } from '@/lib/clinical-insights'
import { calculateHealthScore, fetchHealthEntries, getHealthStatus } from '@/lib/health'
import { getLabResults } from '@/lib/lab-results'
import { getActiveMedications, getPatientMedicationHistory } from '@/lib/medications'

function average(values: number[]) {
  if (values.length === 0) {
    return null
  }

  return values.reduce((total, value) => total + value, 0) / values.length
}

function formatMetricValue(value: number | null, suffix = '') {
  if (value === null || Number.isNaN(value)) {
    return '--'
  }

  const rounded =
    Math.abs(value) >= 10
      ? Number.isInteger(value)
        ? value.toString()
        : value.toFixed(1).replace(/\.0$/, '')
      : value.toFixed(1).replace(/\.0$/, '')

  return `${rounded}${suffix}`
}

function formatShortDate(value: string) {
  return format(new Date(`${value}T12:00:00`), 'dd MMM', { locale: fr })
}

function DashboardStat({
  label,
  value,
  accentClass
}: {
  label: string
  value: string
  accentClass?: string
}) {
  return (
    <div className="rounded-2xl border border-dark-border bg-dark-surface p-4">
      <p className="text-xs uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <p className={`mt-2 text-2xl font-semibold text-white ${accentClass || ''}`}>{value}</p>
    </div>
  )
}

function EmptyState({
  title,
  actionLabel
}: {
  title: string
  actionLabel: string
}) {
  return (
    <div className="rounded-2xl border border-dashed border-dark-border bg-dark-surface-high p-6 text-center">
      <p className="text-sm text-slate-300">{title}</p>
      <Link to="/journal" className="btn-primary mt-4 inline-flex">
        <Mic className="h-4 w-4" />
        {actionLabel}
      </Link>
    </div>
  )
}

function JournalTooltip({
  active,
  payload,
  label
}: {
  active?: boolean
  payload?: Array<{ name?: string; value?: number | null; color?: string }>
  label?: string
}) {
  if (!active || !payload?.length || !label) {
    return null
  }

  const visibleRows = payload.filter(
    (entry) => typeof entry.value === 'number' && Number.isFinite(entry.value)
  )

  if (visibleRows.length === 0) {
    return null
  }

  return (
    <div className="rounded-2xl border border-dark-border bg-[#07111f] p-3 shadow-xl">
      <p className="text-xs font-medium uppercase tracking-[0.16em] text-slate-400">{label}</p>
      <div className="mt-2 space-y-1.5">
        {visibleRows.map((entry) => (
          <div key={entry.name} className="flex items-center justify-between gap-4 text-sm">
            <span className="text-slate-300">{entry.name}</span>
            <span className="font-semibold" style={{ color: entry.color }}>
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function Dashboard() {
  const { user } = useAuth()

  const entriesQuery = useQuery({
    queryKey: ['health-entries', user?.id, 'dashboard-v2'],
    queryFn: () => fetchHealthEntries(user!.id, 45),
    enabled: Boolean(user)
  })

  const labResultsQuery = useQuery({
    queryKey: ['lab-results', user?.id, 'dashboard-v2'],
    queryFn: () => getLabResults(user!.id, 180),
    enabled: Boolean(user)
  })

  const medicationsQuery = useQuery({
    queryKey: ['active-medications', user?.id, 'dashboard-v2'],
    queryFn: () => getActiveMedications(user!.id),
    enabled: Boolean(user)
  })

  const medicationHistoryQuery = useQuery({
    queryKey: ['medication-history', user?.id, 'dashboard-v2'],
    queryFn: () => getPatientMedicationHistory(user!.id),
    enabled: Boolean(user)
  })

  const entries = entriesQuery.data ?? []
  const labResults = labResultsQuery.data ?? []
  const activeMedications = medicationsQuery.data ?? []
  const medicationHistory = medicationHistoryQuery.data ?? []
  const latestEntry = entries[0] ?? null
  const journalScore = calculateHealthScore(latestEntry)
  const journalStatus = getHealthStatus(journalScore)

  const chartData = useMemo(
    () =>
      [...entries]
        .reverse()
        .map((entry) => ({
          date: entry.entry_date,
          label: formatShortDate(entry.entry_date),
          Douleur: entry.pain_level,
          Fatigue: entry.fatigue_level,
          Energie: entry.energy_level,
          Sommeil: entry.sleep_quality
        })),
    [entries]
  )

  const recentEntries = entries.slice(0, 7)
  const recentPain = average(
    recentEntries
      .map((entry) => entry.pain_level)
      .filter((value): value is number => typeof value === 'number')
  )
  const recentEnergy = average(
    recentEntries
      .map((entry) => entry.energy_level)
      .filter((value): value is number => typeof value === 'number')
  )
  const alertCount = entries.filter((entry) => entry.urgent_alert || entry.bleeding_present).length
  const abnormalCount = labResults.filter((result) => result.is_abnormal).length

  const clinicalSummary = useMemo(
    () =>
      buildClinicalDashboardSummary({
        healthEntries: entries,
        activeMedications,
        medicationHistory,
        labResults
      }),
    [entries, activeMedications, medicationHistory, labResults]
  )

  const lastEntryFacts = [
    {
      label: 'Douleur',
      value: formatMetricValue(latestEntry?.pain_level ?? null, '/10')
    },
    {
      label: 'Fatigue',
      value: formatMetricValue(latestEntry?.fatigue_level ?? null, '/10')
    },
    {
      label: 'Energie',
      value: formatMetricValue(latestEntry?.energy_level ?? null, '/10')
    },
    {
      label: 'Sommeil',
      value: formatMetricValue(latestEntry?.sleep_quality ?? null, '/10')
    }
  ]

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-cyan-400" />
            <h1 className="text-2xl font-bold text-white">Tableau de bord</h1>
          </div>
          <p className="text-sm text-slate-400">
            {format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Link to="/journal" className="btn-primary">
            <Mic className="h-4 w-4" />
            Journal quotidien
          </Link>
          <Link to="/lab-results" className="btn-secondary">
            <ClipboardList className="h-4 w-4" />
            Analyse biologique
          </Link>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <DashboardStat label="Score du jour" value={formatMetricValue(journalScore, '/10')} />
        <DashboardStat label="Douleur moyenne 7j" value={formatMetricValue(recentPain, '/10')} />
        <DashboardStat label="Energie moyenne 7j" value={formatMetricValue(recentEnergy, '/10')} />
        <DashboardStat
          label="Alertes / bilans a surveiller"
          value={`${alertCount} / ${abnormalCount}`}
          accentClass={alertCount > 0 || abnormalCount > 0 ? 'text-amber-200' : ''}
        />
      </div>

      <section className="card space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <HeartPulse className="h-4 w-4 text-cyan-300" />
              <h2 className="text-lg font-semibold text-white">Journal quotidien</h2>
            </div>
            <p className="text-sm text-slate-400">
              Donnees utiles uniquement, avec une vue rapide sur l'evolution recente.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className={`badge border ${journalStatus.className}`}>{journalStatus.label}</span>
            <Link to="/journal" className="btn-secondary">
              <Mic className="h-4 w-4" />
              Remplir
            </Link>
          </div>
        </div>

        {chartData.length === 0 ? (
          <EmptyState
            title="Aucune donnee quotidienne pour le moment."
            actionLabel="Commencer le journal"
          />
        ) : (
          <>
            <div className="h-[280px] rounded-2xl border border-dark-border bg-dark-surface-high px-3 py-4">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 12, right: 12, left: -12, bottom: 0 }}>
                  <defs>
                    <linearGradient id="painFill" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="5%" stopColor="#f87171" stopOpacity={0.28} />
                      <stop offset="95%" stopColor="#f87171" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="fatigueFill" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="5%" stopColor="#fb923c" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#fb923c" stopOpacity={0} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid stroke="rgba(148,163,184,0.12)" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 12 }}
                  />
                  <YAxis
                    domain={[0, 10]}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    width={28}
                  />
                  <Tooltip content={<JournalTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="Douleur"
                    stroke="#f87171"
                    fill="url(#painFill)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="Fatigue"
                    stroke="#fb923c"
                    fill="url(#fatigueFill)"
                    strokeWidth={2}
                  />
                  <Area type="monotone" dataKey="Energie" stroke="#38bdf8" fillOpacity={0} strokeWidth={2} />
                  <Area type="monotone" dataKey="Sommeil" stroke="#34d399" fillOpacity={0} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="grid gap-3 md:grid-cols-4">
              {lastEntryFacts.map((fact) => (
                <div key={fact.label} className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
                  <p className="text-xs uppercase tracking-[0.16em] text-slate-500">{fact.label}</p>
                  <p className="mt-2 text-xl font-semibold text-white">{fact.value}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <section className="card space-y-5">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-amber-300" />
          <h2 className="text-lg font-semibold text-white">Lecture clinique</h2>
        </div>

        <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
          <p className="text-sm leading-6 text-slate-200">{clinicalSummary.overview}</p>
        </div>

        <div className="grid gap-4 xl:grid-cols-3">
          <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
            <h3 className="text-sm font-semibold text-white">A surveiller</h3>
            <div className="mt-3 space-y-2">
              {clinicalSummary.watch_items.length > 0 ? (
                clinicalSummary.watch_items.slice(0, 4).map((item) => (
                  <div key={item} className="flex items-start gap-2 text-sm text-slate-300">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-300" />
                    <span>{item}</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500">Aucun point critique remonte pour le moment.</p>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
            <h3 className="text-sm font-semibold text-white">Actions utiles</h3>
            <div className="mt-3 space-y-2">
              {clinicalSummary.action_items.length > 0 ? (
                clinicalSummary.action_items.slice(0, 4).map((item) => (
                  <div key={item} className="text-sm text-slate-300">
                    {item}
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500">Aucune action proposee pour l'instant.</p>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
            <h3 className="text-sm font-semibold text-white">Hypotheses</h3>
            <div className="mt-3 space-y-3">
              {clinicalSummary.hypotheses.length > 0 ? (
                clinicalSummary.hypotheses.slice(0, 3).map((hypothesis) => (
                  <div key={hypothesis.id} className="rounded-2xl border border-dark-border bg-dark-surface p-3">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-medium text-white">{hypothesis.label}</p>
                      <span
                        className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
                          hypothesis.strength === 'forte'
                            ? 'bg-emerald-500/15 text-emerald-200'
                            : hypothesis.strength === 'possible'
                              ? 'bg-amber-500/15 text-amber-200'
                              : 'bg-slate-500/15 text-slate-300'
                        }`}
                      >
                        {hypothesis.strength}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-slate-300">{hypothesis.summary}</p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500">Pas assez de donnees structurees pour une hypothese utile.</p>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
